import { makeAutoObservable, runInAction } from 'mobx'
import { AudienceLiftConversion, CreateConversionRequest, UpdateConversionRequest } from '../types/AudienceLiftConversion'
import { AudienceLiftConversionService } from '../services/audienceLiftConversionService'
import { RootStore } from './rootStore'
import { ToastType } from '../types/ToastData'

const getErrorMessage = (error: unknown): string | undefined => (error as { message?: string })?.message ?? (error ? String(error) : undefined)

export class AudienceLiftConversionStore {
    private conversionService: AudienceLiftConversionService
    private rootStore: RootStore
    conversions: AudienceLiftConversion[] = []
    error: string | null = null
    private loadedCompanyId: number | undefined = undefined

    constructor(rootStore: RootStore, conversionService: AudienceLiftConversionService) {
        this.rootStore = rootStore
        this.conversionService = conversionService
        makeAutoObservable(this)
    }

    loadConversions = async (forceRefresh = false, companyId?: number) => {
        if (this.conversions.length > 0 && !forceRefresh && this.loadedCompanyId === companyId) return this.conversions
        return this.getConversions(companyId)
    }

    // companyId is only passed from the Admin panel, where an admin manages another company's
    // conversions and every call must be scoped with company_id; the company's own view omits it.
    async getConversions(companyId?: number, showToast = true) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const conversions = companyId
                ? await this.conversionService.getConversionsAdmin(companyId)
                : await this.conversionService.getConversions()
            runInAction(() => {
                this.conversions = conversions
                this.loadedCompanyId = companyId
            })
            return conversions
        } catch (error) {
            runInAction(() => {
                const errMsg = getErrorMessage(error)
                if (showToast) {
                    this.rootStore.appStore.setToastNotification({
                        type: ToastType.Error,
                        title: 'Failed to get audience-lift conversions',
                        body: errMsg ?? 'Something went wrong, please try again'
                    })
                }
                this.error = errMsg ?? null
            })
            if (!showToast) throw error
            return []
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async create(payload: CreateConversionRequest, companyId?: number): Promise<AudienceLiftConversion> {
        return companyId ? this.conversionService.createConversionAdmin(companyId, payload) : this.conversionService.createConversion(payload)
    }

    async update(id: number, payload: UpdateConversionRequest, companyId?: number): Promise<AudienceLiftConversion> {
        return companyId
            ? this.conversionService.updateConversionAdmin(companyId, id, payload)
            : this.conversionService.updateConversion(id, payload)
    }

    async remove(id: number, companyId?: number): Promise<boolean> {
        companyId ? await this.conversionService.removeConversionAdmin(companyId, id) : await this.conversionService.removeConversion(id)
        return true
    }

    async assignEvents(id: number, eventIds: number[], companyId?: number): Promise<AudienceLiftConversion> {
        return companyId
            ? this.conversionService.assignConversionEventsAdmin(companyId, id, eventIds)
            : this.conversionService.assignConversionEvents(id, eventIds)
    }
}
